import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { isFulfilled } from "@reduxjs/toolkit";

import type { Student } from "types/Student";
import { CLASS_PATH } from "router/pathName";
import { STUDENT } from "config/keywords";
import ProfileAvatar from "components/__reusable/ProfileAvatar";
import { getTrainingPictures } from "services/trainingPictureServices";
import ProfilePictureDialog from "./ProfilePictureDialog";
import type { TrainingPicture } from "types/TrainingPicture";
import useAppDispatch from "hooks/useAppDispatch";
import { changeProfilePicture } from "features/student/studentAction";
import {
  getDefaultStudentProfilePicture,
  getRelativeProfilePicturePath,
} from "utilities/StudentProfilePicture";

interface StudentInfoProps {
  student: Student;
}

const StudentInfo: React.FC<StudentInfoProps> = ({ student }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const autoProfileAttemptedRef = useRef<number | null>(null);
  const [defaultProfilePicture, setDefaultProfilePicture] = useState("");
  const renderStudentName = (student: Student) => {
    const fullName = `${student.last_name} ${student.first_name}`;

    if (student.first_name_kata && student.last_name_kata) {
      return (
        <>
          <span className="underline">
            {fullName}
          </span>
          <br />
          <span style={{ fontSize: "0.9em" }} className="text-[#5B5B5B]">
            {student.last_name_kata} {student.first_name_kata}
          </span>
        </>
      );
    }

    return (
      <span className="underline">
        {fullName}
      </span>
    );
  };

  const [trainingPictures, setTrainingPictures] = useState<TrainingPicture[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  const fetchTrainingPictures = useCallback(async () => {
    setIsLoading(true);
    setError(false);
    try {
      const pictures = await getTrainingPictures(student.id.toString());
      const usablePictures = pictures.filter((picture) => picture.is_usable);
      setTrainingPictures(usablePictures);
    } catch (e) {
      setError(true);
    } finally {
      setIsLoading(false);
    }
  }, [student.id]);

  useEffect(() => {
    fetchTrainingPictures();
  }, [fetchTrainingPictures]);

  useEffect(() => {
    const defaultPicture = getDefaultStudentProfilePicture(
      student.profile_picture_for_admin,
      trainingPictures,
    );
    if (!defaultPicture || autoProfileAttemptedRef.current === student.id) {
      return;
    }

    autoProfileAttemptedRef.current = student.id;
    setDefaultProfilePicture(defaultPicture);
    dispatch(
      changeProfilePicture({
        id: student.id,
        picture: getRelativeProfilePicturePath(defaultPicture),
      }),
    ).then((action) => {
      if (!isFulfilled(action)) {
        setDefaultProfilePicture("");
        setError(true);
      }
    });
  }, [
    dispatch,
    student.id,
    student.profile_picture_for_admin,
    trainingPictures,
  ]);

  const [isOpenDialog, setIsOpenDialog] = useState(false);

  return (
    <div className="flex items-center space-x-2">
      <div className="relative">
        <button
          type="button"
          className="block rounded-full"
          aria-label={t("_student._please_select_picture")}
          onClick={() => setIsOpenDialog(true)}
        >
          <ProfileAvatar
            pictureUrl={
              student.profile_picture_for_admin || defaultProfilePicture
            }
            hoverBrighten={true}
          />
        </button>
        <span className="pointer-events-none flex items-center justify-center w-4 h-4 absolute bottom-0 right-0 text-white text-xs bg-[#4192ee] rounded-full">
          +
        </span>
      </div>
      <div>
        <Link
          to={`${CLASS_PATH}/${student.class_id}/${STUDENT}/${student.id}`}
          className="text-[#5B5B5B] transition-colors duration-200 ease-out hover:text-[#00A09B]"
        >
          {renderStudentName(student)}
        </Link>
        <p className="text-sm">
          {isLoading && "Loading..."}
          {error && t("_generic._unknown_error")}
        </p>
      </div>
      {isOpenDialog && (
        <ProfilePictureDialog
          studentId={student.id}
          pictures={trainingPictures.map((picture) => picture.original_picture)}
          onClose={() => setIsOpenDialog(false)}
        />
      )}
    </div>
  );
};

export default StudentInfo;
